import type { Api } from '@proton/shared/lib/interfaces';

import { PAYMENT_METHOD_TYPES } from '../constants';
import { type PaymentVerificatorV5, createPaymentTokenForExistingChargebeePayment } from '../createPaymentToken';
import type {
    AmountAndCurrency,
    ChargeablePaymentParameters,
    ChargeableV5PaymentParameters,
    ChargebeeIframeEvents,
    ChargebeeIframeHandles,
    SavedMethodType,
    SavedPaymentMethod,
    V5PaymentToken,
} from '../interface';
import { PaymentProcessor } from './paymentProcessor';

interface SavedChargebeePaymentState {
    method: {
        paymentMethodId: string;
        type: SavedMethodType;
    };
}

export class SavedChargebeePaymentProcessor extends PaymentProcessor<SavedChargebeePaymentState> {
    public fetchedPaymentToken: any;

    constructor(
        public verifyPayment: PaymentVerificatorV5,
        public api: Api,
        public handles: ChargebeeIframeHandles,
        public events: ChargebeeIframeEvents,
        amountAndCurrency: AmountAndCurrency,
        /**
         * For the on-session v4-v5 migration, the saved payment method can also be internal.
         * In this case the frontend will send the internal saved method to v5 and the backend will do the migration.
         */
        savedMethod: SavedPaymentMethod,
        public onTokenIsChargeable: (data: ChargeablePaymentParameters) => Promise<unknown>,
        public onDeclined: () => void
    ) {
        super(
            {
                method: {
                    paymentMethodId: savedMethod.ID,
                    type: savedMethod.Type,
                },
            },
            amountAndCurrency
        );
    }

    async fetchPaymentToken() {
        if (this.amountAndCurrency.Amount === 0) {
            return null;
        }

        try {
            this.fetchedPaymentToken = await createPaymentTokenForExistingChargebeePayment(
                this.state.method.paymentMethodId,
                this.state.method.type,
                this.api,
                this.handles,
                this.events,
                this.amountAndCurrency
            );
        } catch (error) {
            this.onDeclined();
            throw error;
        }

        return this.fetchedPaymentToken;
    }

    async verifyPaymentToken(): Promise<ChargeablePaymentParameters> {
        if (this.amountAndCurrency.Amount === 0) {
            return this.tokenCreated();
        }

        if (this.fetchedPaymentToken === null) {
            throw new Error('Payment token was not fetched. Please call fetchPaymentToken() first.');
        }

        try {
            // We can't avoid this step, because we need to request the token status at least once
            // to make sure that it is actually chargebable
            const token = await this.verifyPayment({
                token: this.fetchedPaymentToken,
                events: this.events,
                v: 5,
                paymentMethodType: this.state.method.type,
                paymentMethodValue: this.state.method.paymentMethodId,
            });

            return this.tokenCreated(token);
        } catch (error) {
            this.onDeclined();
            throw error;
        }
    }

    updateSavedMethod(savedMethod: SavedPaymentMethod) {
        this.state.method = {
            paymentMethodId: savedMethod.ID,
            type: savedMethod.Type,
        };
    }

    reset() {
        this.fetchedPaymentToken = null;
    }

    private tokenCreated(token?: V5PaymentToken): ChargeableV5PaymentParameters {
        const result: ChargeableV5PaymentParameters = {
            type: PAYMENT_METHOD_TYPES.CHARGEBEE_CARD,
            chargeable: true,
            ...this.amountAndCurrency,
            ...token,
            v: 5,
        };

        void this.onTokenIsChargeable?.(result);

        return result;
    }
}
